import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export type SharedWorkflowKey =
  | 'systemAssetCapture'
  | 'dataAssetCapture'
  | 'dataAssetConfiguration'
  | 'securityZoneManagement';

export interface SharedWorkflowItem {
  key: SharedWorkflowKey;
  path: string;
}

export interface SharedWorkflowProductConfig {
  routePrefix: string;
  basePath: string;
  title: string;
  workflows: SharedWorkflowItem[];
}

@Injectable({
  providedIn: 'root'
})
export class WorkflowNavigationService {

  /**
   * A single source of truth for workflow availability.
   *
   * Only list workflows that the corresponding product
   * has registered in its Angular router.
   */
  private readonly productConfigs: SharedWorkflowProductConfig[] = [
    {
      routePrefix: '/asset-management/auth-tools',
      basePath: '/asset-management/auth-tools',
      title: 'Asset Management',
      workflows: [
        {
          key: 'systemAssetCapture',
          path: 'system-asset-capture'
        },
        {
          key: 'dataAssetCapture',
          path: 'data-asset-capture'
        },
        {
          key: 'dataAssetConfiguration',
          path: 'data-asset-configuration'
        }
      ]
    },

    {
      routePrefix: '/data-protection/auth-tools',
      basePath: '/data-protection/auth-tools',
      title: 'Data Protection',
      workflows: [
        {
          key: 'systemAssetCapture',
          path: 'system-asset-capture'
        },
        {
          key: 'dataAssetCapture',
          path: 'data-asset-capture'
        },
        {
          key: 'dataAssetConfiguration',
          path: 'data-asset-configuration'
        },
        {
          key: 'securityZoneManagement',
          path: 'security-zone-management',
        }
      ]
    },

    {
      routePrefix: '/secure-zone',
      basePath: '/secure-zone',
      title: 'Security Zone',
      workflows: [
        {
          key: 'securityZoneManagement',
          path: 'security-zone-management',
        },
      ],
    }

    // Example: another product exposes only Data Asset Capture.

  ];

  constructor(private readonly router: Router) { }

  /**
   * Resolve the current product from the URL.
   */
  getCurrentProductConfig(): SharedWorkflowProductConfig | undefined {
    const currentUrl = this.router.url.split(/[?#]/)[0];

    return this.productConfigs.find(config =>
      currentUrl === config.routePrefix ||
      currentUrl.startsWith(`${config.routePrefix}/`)
    );
  }

  /**
 * Build a URL for a workflow or one of its child routes
 * without hard-coding the hosting product's route prefix.
 *
 * Examples:
 * getWorkflowUrl('securityZoneManagement')
 * getWorkflowUrl('securityZoneManagement', ['platform', 'search'])
 */
  getWorkflowUrl(
    key: SharedWorkflowKey,
    childSegments: Array<string | number> = []
  ): string | null {
    const config = this.getCurrentProductConfig();

    if (!config) {
      return null;
    }

    const workflow = config.workflows.find(
      item => item.key === key
    );

    if (!workflow) {
      return null;
    }

    const segments = [
      config.basePath,
      workflow.path,
      ...childSegments.map(segment =>
        encodeURIComponent(String(segment))
      ),
    ]
      .map(segment => segment.replace(/^\/+|\/+$/g, ''))
      .filter(Boolean);

    return `/${segments.join('/')}`;
  }

  /**
   * Check whether the current product supports a workflow.
   */
  hasWorkflow(key: SharedWorkflowKey): boolean {
    const config = this.getCurrentProductConfig();

    return config?.workflows.some(
      workflow => workflow.key === key
    ) ?? false;
  }

  /**
   * Navigate to a workflow or one of its child routes.
   *
   * Examples:
   * navigateToWorkflow('dataAssetCapture')
   * navigateToWorkflow('dataAssetCapture', ['create'])
   * navigateToWorkflow('dataAssetConfiguration',
   *   ['configure-data-asset', 123, 'NOT_CONFIGURED'])
   */
  navigateToWorkflow(
    key: SharedWorkflowKey,
    childSegments: Array<string | number> = []
  ): Promise<boolean> {
    const config = this.getCurrentProductConfig();

    if (!config) {
      console.error(
        'Cannot navigate to asset workflow: current product was not identified.'
      );

      return Promise.resolve(false);
    }

    const workflow = config.workflows.find(
      item => item.key === key
    );

    if (!workflow) {
      console.warn(
        `Workflow "${key}" is not enabled for ${config.title}.`
      );

      return Promise.resolve(false);
    }

    const encodedSegments = childSegments.map(
      segment => encodeURIComponent(String(segment))
    );

    const targetUrl = [
      config.basePath,
      workflow.path,
      ...encodedSegments
    ].join('/');

    return this.router.navigateByUrl(targetUrl);
  }

  /**
   * Return to the current product's authoring-tools root.
   * This assumes its empty child route opens the dashboard.
   */
  navigateToProductHome(): Promise<boolean> {
    const config = this.getCurrentProductConfig();

    if (!config) {
      console.error(
        'Cannot return to product home: current product was not identified.'
      );

      return Promise.resolve(false);
    }

    return this.router.navigateByUrl(config.basePath);
  }
}